import apiClient from "../clients/apiClient";
import { ApiResponse } from "../schemas/apiResponseSchema";

const getErrorMessage = (error: any, fallbackMessage: string) => {
    const detail = error?.response?.data?.detail;
    if (detail) {
        return typeof detail === "string" ? detail : JSON.stringify(detail);
    }

    if (error?.message) {
        return error.message;
    }

    if (typeof error === "string") {
        return error;
    }

    if (error?.toString) {
        return error.toString();
    }

    return fallbackMessage;
};

export interface CashReceiptResponse {
    payment_no?: number | null;
    payment_loc?: string | null;
    payment_date?: string | null;
    payment_time?: string | null;
    payment_cashier_id?: string | null;
    payment_station?: string | null;
    payment_shift?: number | null;
    payment_description?: string | null;
    payment_amount?: number | null;
    payment_mode?: string | null;
    created_at?: string | null;
    crec_crdate?: string | null;
    crec_crby?: string | null;
    crec_mddate?: string | null;
    crec_mdby?: string | null;
}

export type CashReceiptCreateRequest = {
    payment_loc: string;
    payment_date: string;
    payment_time: string;
    payment_cashier_id: string;
    payment_station: string;
    payment_shift: number;
    payment_description: string;
    payment_amount: number;
    payment_mode: string;
};

export interface CashReceiptFilters {
    payment_loc?: string;
    payment_date?: string;
    payment_station?: string | number;
    payment_shift?: number;
}

export const getAllCashReceipts = async (filters?: CashReceiptFilters): Promise<CashReceiptResponse[]> => {
    try {
        const params = new URLSearchParams();
        if (filters) {
            if (filters.payment_loc) params.append("payment_loc", filters.payment_loc);
            if (filters.payment_date) params.append("payment_date", filters.payment_date);
            if (filters.payment_station !== undefined) params.append("payment_station", String(filters.payment_station));
            if (filters.payment_shift !== undefined) params.append("payment_shift", String(filters.payment_shift));
        }
        
        const queryString = params.toString();
        const url = queryString ? `/pos_cash_rec_with/all?${queryString}` : "/pos_cash_rec_with/all";
        
        const response: ApiResponse<CashReceiptResponse[] | null> = (
            await apiClient.get(url, {
                headers: {
                    "x-skip-auth-redirect": "true",
                },
            })
        ).data;

        return response.data || [];
    } catch (error: any) {
        if (error?.response?.status === 401) {
            throw new Error("Not authorized to load cash receipts. Please sign in with backend authentication.");
        }

        throw new Error(getErrorMessage(error, "Failed to fetch cash receipts"));
    }
};

export const createCashReceipt = async (receiptData: CashReceiptCreateRequest): Promise<{ payment_no: number }> => {
    try {
        const response: ApiResponse<{ payment_no: number }> = (
            await apiClient.post("/pos_cash_rec_with/create", receiptData, {
                headers: {
                    "x-skip-auth-redirect": "true",
                },
            })
        ).data;

        return response.data;
    } catch (error: any) {
        if (error?.response?.data?.detail) {
            const detail = error.response.data.detail;
            const message = typeof detail === 'string' ? detail : JSON.stringify(detail);
            throw new Error(message);
        }

        throw new Error("Failed to create cash receipt");
    }
};

export const getCashReceiptById = async (receiptId: number): Promise<CashReceiptResponse | null> => {
    try {
        const response: ApiResponse<CashReceiptResponse | null> = (
            await apiClient.get(`/pos_cash_rec_with/${receiptId}`, {
                headers: {
                    "x-skip-auth-redirect": "true",
                },
            })
        ).data;

        return response.data || null;
    } catch (error: any) {
        if (error?.response?.data?.detail) {
            const detail = error.response.data.detail;
            const message = typeof detail === 'string' ? detail : JSON.stringify(detail);
            throw new Error(message);
        }

        throw new Error("Failed to fetch cash receipt");
    }
};